import ReducersIndex from "reduxModules/ducks";
import constants from "./constants";
import { keys } from "lodash";
import { maxExpansionMicCountFor, maxExpansionSpeakerCountFor } from "./treeFrogPorts";

/**
 * Object containing available actions for different views in the Conference Room Designer.
 * @constant
 * @type {Object.<string, Object.<string, string[]>>}
 */
const actions = {
  floorplan: {
    area: ['measure'],
  },
  ceiling: {
    area: ['measure', 'block']
  },
  furnishing: {
    area: ['measure', 'armedghostchair']
  },
  acoustics: {
    area: ['measure'],
    // Dynamic actions will be determined based on currentDevice
    microphones: ['measure', 'position', 'distance', 'move', 'add', 'remove', 'listen'],
    speakers: ['measure', 'position', 'relativelevel', 'move', 'add', 'remove'],
    cameras: ['measure', 'position', 'level', 'distance', 'move', 'add', 'remove']
  },
  microphones: {
    area: ['measure', 'position', 'distance', 'move', 'add', 'remove', 'delete', 'listen']
  },
  speakers: {
    area: ['measure', 'position', 'relativelevel', 'move', 'add', 'remove', 'delete']
  },
  camera: {
    area: ['measure', 'position', 'level', 'distance', 'move', 'add', 'remove']
  },
  equipmentlist: {}
};

/**
 * Conditions for enabling certain actions based on the current state.
 * These conditions are passed from the actionBar component to the buttonGroup component,
 * which uses the getAvailableActions selector to determine which actions should be visible.
 * @constant
 * @type {Object.<string, {parameter: string[], check: function}>}
 */
export const conditions = {
  /**
   * Condition for the 'block' action.
   * Enabled when ceiling tiles are present.
   * @param {Array} tiles - The ceiling tiles state.
   * @returns {boolean} True if ceiling tiles are present, false otherwise.
   */
  block: { parameter: ['ceilingTiles'], check: ([tiles]) => tiles.tile !== 'no_ceiling_tiles' },

  /**
   * Condition for the 'armedghostchair' action.
   * Enabled when a table shape is defined.
   * @param {Object} table - The table state.
   * @returns {boolean} True if a table shape is defined, false otherwise.
   */
  armedghostchair: { parameter: ['table'], check: ([table]) => {
    return !!table.shape;
  } },

  /**
 * Condition for the 'position' action.
 * Enabled when the current device is 'speakers'.
 * @param {string} device - The current device.
 * @returns {boolean} True if the current device is 'speakers', false otherwise.
 */
  position: { parameter: ['currentDevice',], check: ([device]) => keys(constants.equipment).includes(device) },


  /**
   * Condition for the 'level' action.
   * Enabled when the current device is 'speakers'.
   * @param {string} device - The current device.
   * @returns {boolean} True if the current device is 'speakers', false otherwise.
   */
  level: { parameter: ['currentDevice',], check: ([device]) => device === 'speakers' },

  /**
   * Condition for the 'relativelevel' action.
   * Enabled only when the current device is 'speakers'.
   * @param {string} device - The current device.
   * @returns {boolean} True if the current device is 'speakers', false otherwise.
   */
  relativelevel: { parameter: ['currentDevice',], check: ([device]) => device === 'speakers' },

  /**
   * Condition for the 'distance' action.
   * Enabled when the current device is 'microphones' or 'cameras'.
   * @param {string} device - The current device.
   * @returns {boolean} True if the current device supports distance functionality, false otherwise.
   */
  distance: { parameter: ['currentDevice',], check: ([device]) => ['microphones', 'cameras'].includes(device) },

  /**
   * Condition for the 'move' action.
   * The availability of the 'move' action depends on the current device and the specific device model.
   *
   * @param {string} device - The current device ('microphones', 'speakers', 'cameras', or others).
   * @param {string} micModel - The current microphone model key (used for all device types).
   * @param {string} micModelName - The current microphone model name (used for all device types).
   * @param {string} speakerModelName - The current speaker model name (only relevant when device is 'speakers').
   * @param {string} cameraModel - The current camera model key (only relevant when device is 'cameras').
   * @returns {boolean} True if the 'move' action should be enabled, false otherwise.
   *
   * @description
   * - For microphones: Disabled only for CBC 2500 CM/PM bar microphones and table microphones.
   * - For speakers: Disabled only for CBC 2500 CM/PM bar speakers (tied to CBC bar microphone systems).
   * - For cameras: Disabled only for CBC 2500 CM bar cameras (integrated camera). CBC 2500 PM uses external camera which can be moved.
   * - For all other bar systems (VBC 2500a, ABC 2500a, VBC 2800): Move button is enabled.
   */
  move: {
    parameter: ['currentDevice', 'microphones.model', 'microphones.modelName', 'speakers.modelName', 'cameras.model', 'expansionMicType'],
    check: ([device, micModel, , , , expansionMicType]) => {
      if (!keys(constants.equipment).includes(device)) return false;

      if (device === 'microphones') {
        if (!micModel) return false;
        const { micCategories } = constants.equipment;
        // Hide Move for table mics and CBC ceiling-bar mics (fixed position)
        if (micCategories.TABLETOP.has(micModel)) return false;
        if (micCategories.CBC.has(micModel)) return false;
        // Hide Move when a bar mic has table expansion mics — those positions are derived
        // from the table geometry (calcExpansionTableMicPlacement), so manual moves don't
        // make sense the way they do for ceiling expansion. The bar itself isn't movable either.
        if (expansionMicType === 'table') return false;
        return true;
      }

      // For speakers, hide Move only for CBC bar systems (ceiling-mounted, fixed)
      if (device === 'speakers') {
        const { micCategories } = constants.equipment;
        if (micModel && micCategories.CBC.has(micModel)) return false;
        return true;
      }

      // For cameras on bar systems, always show move button
      if (device === 'cameras') {
        // Allow moving cameras for all bar systems including CBC 2500 CM
        return true;
      }

      return true;
    }
  },

  /**
   * Condition for the 'add' action.
   * The availability of the 'add' action depends on the current device and the specific device model.
   *
   * @param {string} device - The current device ('microphones', 'speakers', 'cameras', or others).
   * @param {string} micModel - The current microphone model (only relevant when device is 'microphones').
   * @param {string} speakerModel - The current speaker model (only relevant when device is 'speakers').
   * @returns {boolean} True if the 'add' action should be enabled, false otherwise.
   *
   * @description
   * - For microphones: Enabled when the microphone is not a bar or table microphone.
   * - For speakers: Enabled when the speaker is not a bar type and not a wall-mounted speaker.
   * - For cameras: Always disabled.
   * - For other devices: Always enabled.
   */
  add: {
    parameter: ['currentDevice', 'microphones.model', 'speakers.model', 'speakers.mountingType', 'expansionMicType', 'expansionMicrophones', 'expansionSpeakerType', 'expansionSpeakers'], check: ([device, micModel, speakerModel, mountingType, expansionMicType, expansionMicrophones, expansionSpeakerType, expansionSpeakers]) => {
      switch (device) {
        case 'microphones':
          if (!micModel) return false;
          const { micCategories: addMicCategories } = constants.equipment;
          const isBar = addMicCategories.BAR.has(micModel);
          const isCbc = addMicCategories.CBC.has(micModel);
          const isTabletop = addMicCategories.TABLETOP.has(micModel);
          // Hide Add when a bar mic has table expansion mics — placement is derived from the
          // table geometry, so manually dropping a mic at the click location doesn't fit the
          // model. Ceiling expansion still allows Add so users can pin additional ceiling mics.
          if (isBar && expansionMicType === 'table') return false;
          // Bar + (ceiling expansion already active OR not yet configured — the
          // canvas click-to-place flow opens the add dialog/places the first mic
          // as 'ceiling'): cap at `maxExpansionMics` (4 today) AND at the UCC
          // port budget given the current expansion speaker count (TC-05:
          // Tesira has no bar + expansion equivalent, so a design that has no
          // port left to add even one mic without overflowing must not show
          // Add as available at all — otherwise the click/dialog just leads to
          // 0 mics actually landing, per the same cap `addExpansionMic` /
          // `setExpansionMicLayout` enforce). Table is excluded above already.
          if (isBar && !isCbc && expansionMicType !== 'table') {
            const placedMicCount = expansionMicrophones?.layout?.length ?? 0;
            const micCap = constants.maxExpansionMics ?? Infinity;
            if (placedMicCount >= micCap) return false;
            // Forced to 'ceiling' regardless of the current (possibly
            // not-yet-configured) value — that's the only type this manual-add
            // flow can start (table has its own scenario-driven placement,
            // gated out above), and it's what `maxExpansionMicCountFor` needs
            // to count chains for the hypothetical mic being added.
            const pseudoArea = {
              microphones: { model: micModel },
              expansionMicType: 'ceiling',
              expansionMicrophones,
              expansionSpeakerType,
              expansionSpeakers,
            };
            if (placedMicCount >= maxExpansionMicCountFor(pseudoArea, micCap)) return false;
          }
          // Non-bar / non-tabletop mics: enable normal add. Non-CBC bar mics: also enable, so the
          // user can open the expansion recommendation dialog (no expansion yet) or directly add an
          // expansion mic at the clicked location (expansion already configured). See the `add`
          // dispatcher below for the branch logic.
          return (!isBar && !isTabletop) || (isBar && !isCbc);
        case 'speakers':
          const speakerConfig = constants.equipment.speakers[speakerModel];
          const isBarSpeaker = speakerConfig?.isBarSpkr ?? false;
          if (isBarSpeaker) {
            // Mirrors the mic-side symmetry: action button shows for non-CBC bars
            // regardless of whether expansion is configured yet. The dispatcher branches —
            // no expansion → opens `expansionSpeakerAdd` dialog; expansion already
            // configured → adds an expansion speaker at the clicked location. Caps at
            // `maxExpansionSpeakers` AND at the UCC port budget given the current
            // expansion mic count (TC-05: Tesira has no bar + expansion equivalent, so
            // Add must not be offered once there's no port left for another speaker).
            const { micCategories: spkrMicCategories } = constants.equipment;
            if (spkrMicCategories.CBC.has(micModel)) return false;
            const isExpansionSpkrActive = expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant';
            const placedCount = expansionSpeakers?.layout?.length ?? 0;
            const cap = constants.maxExpansionSpeakers ?? Infinity;
            if (isExpansionSpkrActive && placedCount >= cap) return false;
            // TC-05: cap at the port budget too, whether expansion is already
            // active or about to be opened via the Add dialog (which starts
            // as 'ceiling' — forced here for the not-yet-configured case so
            // `maxExpansionSpeakerCountFor` counts the hypothetical speaker
            // correctly). Otherwise Add would be offered only to lead to 0
            // speakers actually landing, per the same cap `addExpansionSpeaker`
            // / `setExpansionSpeakerLayout` enforce.
            const pseudoArea = {
              microphones: { model: micModel },
              expansionMicType,
              expansionMicrophones,
              expansionSpeakerType: isExpansionSpkrActive ? expansionSpeakerType : 'ceiling',
              expansionSpeakers,
            };
            return placedCount < maxExpansionSpeakerCountFor(pseudoArea, cap);
          }
          const isNotWallSpeaker = mountingType !== 'wall';
          return !isBarSpeaker && isNotWallSpeaker;
        case 'cameras':
          return false;
        default:
          return false;
      }
    }
  },

  /**
   * Condition for the 'remove' action.
   * The availability of the 'remove' action depends on the current device and the specific device model.
   *
   * @param {string} device - The current device ('microphones', 'speakers', 'cameras', or others).
   * @param {string} micModel - The current microphone model (only relevant when device is 'microphones').
   * @param {string} speakerModel - The current speaker model (only relevant when device is 'speakers').
   * @returns {boolean} True if the 'remove' action should be enabled, false otherwise.
   *
   * @description
   * - For microphones: Enabled when the microphone is not a bar or table microphone.
   * - For speakers: Enabled when the speaker is not a bar type and not a wall-mounted speaker.
   * - For cameras: Always disabled.
   * - For other devices: Always enabled.
   */
  remove: {
    parameter: ['currentDevice', 'microphones.model', 'speakers.model', 'speakers.mountingType'], check: ([device, micModel, speakerModel, mountingType]) => {
      switch (device) {
        case 'microphones':
          if (!micModel) return false;
          const { micCategories: removeMicCategories } = constants.equipment;
          return !removeMicCategories.BAR.has(micModel) && !removeMicCategories.TABLETOP.has(micModel);
        case 'speakers':
          const speakerConfig = constants.equipment.speakers[speakerModel];
          const isNotBarSpeaker = !(speakerConfig?.isBarSpkr ?? false);
          const isNotWallSpeaker = mountingType !== 'wall';
          return isNotBarSpeaker && isNotWallSpeaker;
        case 'cameras':
          return false;
        default:
          return false;
      }
    }
  },

  /**
   * Condition for the 'delete' action.
   * Enabled only on the microphones tab when a non-CBC bar mic has expansion mics placed.
   * Distinct from 'remove' (which is hidden for bar mics) — 'delete' surfaces a way to remove
   * individual expansion mics without giving up the ability to keep the bar mic itself.
   * @param {string} device - The current device.
   * @param {string} micModel - The current microphone model key.
   * @param {string} expansionMicType - 'ceiling' | 'table' | 'none' | null
   * @param {Object} expansionMicrophones - { qty, layout } | null
   * @returns {boolean}
   */
  delete: {
    parameter: ['currentDevice', 'microphones.model', 'expansionMicType', 'expansionMicrophones', 'expansionSpeakerType', 'expansionSpeakers'],
    check: ([device, micModel, expansionMicType, expansionMicrophones, expansionSpeakerType, expansionSpeakers]) => {
      if (!micModel) return false;
      const { micCategories } = constants.equipment;
      const isBar = micCategories.BAR.has(micModel);
      const isCbc = micCategories.CBC.has(micModel);

      // The bar gate is applied per-branch rather than at the top of the function so a
      // future device branch (e.g. cameras with their own expansion concept) doesn't
      // silently inherit the bar-mic requirement. Each branch is responsible for its
      // own bar-mic vs other-config decision.

      // Microphones view: enable Delete when ceiling expansion mics are placed.
      if (device === 'microphones') {
        if (!isBar || isCbc) return false;
        // Hide Delete for table expansion — table mic positions are derived from the table
        // geometry (calcExpansionTableMicPlacement), so removing individual mics doesn't fit
        // the model. Removal is via the menu's Remove Expansion Microphones button, which
        // clears the entire expansion via setExpansionMicType('none').
        if (expansionMicType === 'table') return false;
        // Honour either qty or layout.length — both are kept in sync by the reducers, but they
        // arrive in state slightly differently across action flows (setExpansionMicLayout via
        // performDeviceCalculation vs. direct addExpansionMic / removeExpansionMic). Treating
        // either >= 1 as "placed" makes the button reliably visible whenever the user has at
        // least one expansion mic — including the last remaining one before deletion.
        const qty = expansionMicrophones?.qty ?? 0;
        const layoutLen = expansionMicrophones?.layout?.length ?? 0;
        const hasPlacedMics = qty > 0 || layoutLen > 0;
        return expansionMicType === 'ceiling' && hasPlacedMics;
      }

      // Speakers view: enable Delete when expansion speakers are placed. Both ceiling
      // and pendant types use the same per-device click-to-delete UX (parallel to mic
      // ceiling); the action dispatcher routes to removeExpansionSpeaker either way.
      if (device === 'speakers') {
        if (!isBar || isCbc) return false;
        const isExpansionSpkrActive = expansionSpeakerType === 'ceiling' || expansionSpeakerType === 'pendant';
        if (!isExpansionSpkrActive) return false;
        const spkrQty = expansionSpeakers?.qty ?? 0;
        const spkrLayoutLen = expansionSpeakers?.layout?.length ?? 0;
        return spkrQty > 0 || spkrLayoutLen > 0;
      }

      return false;
    }
  },

  /**
   * Condition for the 'listen' action.
   * Enabled when the current device is 'microphones', except for BMA360D ceiling tile
   * mics (MIC_CATEGORIES.CEILING_TILE) — the listen/pattern-preview icon doesn't apply to them.
   * @param {string} device - The current device.
   * @param {string} micModel - The current microphone model key.
   * @returns {boolean} True if the 'listen' action should be enabled, false otherwise.
   */
  listen: {
    parameter: ['currentDevice', 'microphones.model'],
    check: ([device, micModel]) => {
      if (device !== 'microphones') return false;
      const { micCategories } = constants.equipment;
      if (micModel && micCategories.CEILING_TILE.has(micModel)) return false;
      return true;
    }
  },
};

/**
 * Action dispatchers for specific actions in the Conference Room Designer.
 * @constant
 * @type {Object.<string, function>}
 */
export const actionDispatchers = {
  /**
   * Dispatcher for the measure action.
   * Placeholder implementation for measuring distances.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  measure: () => {
    console.log('Measure tool activated');
    // TODO: Implement measure functionality
  },

  /**
   * Dispatcher for the position action.
   * Shows positioning information for selected devices.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  position: (evt, dispatch, mouse, drawingBox, device) => {
    // Set the UI action to position mode
    dispatch(ReducersIndex.uiReducers.setAction('position'));
    
    // The actual position display logic will be handled by the MouseHandler
    // and the MuiPositionDisplay component
    console.log('Position info tool activated for:', device);
  },

  /**
   * Dispatcher for the distance action.
   * Shows distance information for devices.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  distance: (evt, dispatch, mouse, drawingBox, device) => {
    // Set the UI action to distance mode
    dispatch(ReducersIndex.uiReducers.setAction('distance'));
    
    // The actual distance display logic will be handled by the MouseHandler
    // and the MuiDistanceDisplay component
    console.log('Distance info tool activated for:', device);
  },

  /**
   * Dispatcher for the level action.
   * Shows audio level information for speakers.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  level: (evt, dispatch, mouse, drawingBox, device) => {
    // Set the UI action to level mode
    dispatch(ReducersIndex.uiReducers.setAction('level'));
    
    // The actual level display logic will be handled by the MouseHandler
    // and the MuiPlotLevelDisplay component
    console.log('Level info tool activated for:', device);
  },

  /**
   * Dispatcher for the relativelevel action.
   * Shows relative average level information for speakers.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  relativelevel: (evt, dispatch, mouse, drawingBox, device) => {
    // Set the UI action to relativelevel mode
    dispatch(ReducersIndex.uiReducers.setAction('relativelevel'));
    
    // The actual relative level display logic will be handled by the MouseHandler
    // and the MuiPlotLevelDisplay component
    console.log('Relative level info tool activated for:', device);
  },

  /**
   * Dispatcher for the move action.
   * Move operations are handled by pointer events in MuiDevices.tsx (onPointerDown/Move/Up).
   * This dispatcher is a no-op to prevent premature moveDevice dispatches during drag operations.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  move: () => {
    // No-op: Actual move logic is handled by MuiDevices pointer events
    // Dispatching moveDevice here causes plots to calculate for intermediate
    // positions during drag instead of the final release position
    console.log('Move mode activated - drag devices to reposition');
  },

  /**
   * Dispatcher for the listen action.
   * Shows listening pattern information for microphones.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  listen: () => {
    console.log('Listen pattern tool activated');
    // TODO: Implement listen pattern display
  },

  /**
   * Dispatcher for the block action.
   * Blocks/unblocks ceiling tiles.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse position.
   * @param {Object} drawingBox - Information about the drawing area.
   * @param {string} device - The current device type.
   */
  block: () => {
    console.log('Block tool activated');
    // TODO: Implement ceiling tile blocking
  },

  /**
   * Dispatcher for the armedghostchair action.
   * Toggles the ghost chair status when a chair is clicked.
   * @param {Event} evt - The event object.
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} mouse - The mouse state object (unused in this function).
   */
  armedghostchair: (evt, dispatch) => {
    const chair = evt.target.closest('[class=chair]');
    const chairId = chair?.id;
    const isGhosted = parseInt(chair?.getAttribute('stroke-dasharray')) !== 0;
    if (chairId) dispatch(ReducersIndex.container.setTable({ chair: chairId, isGhosted }));
  },

  /**
   * Dispatcher for the add device action.
   * Adds a new device to the room at the specified mouse position.
   * @param {Event} evt - The event object (not used directly in the function but may be required by the caller)
   * @param {Function} dispatch - The Redux dispatch function to trigger actions
   * @param {Object} mouse - The current mouse position
   * @param {Object} drawingBox - Information about the drawing area
   * @param {Object} drawingBox.bbox - The bounding box of the drawing area
   * @param {string} device - The type of device to add (e.g., 'speaker', 'microphone')
   */
  add: (evt, dispatch, mouse, drawingBox, device) => {
    const { bbox: { x0, y0 }, scale } = drawingBox
    const location = {
      x: (mouse.x - x0) / scale,
      y: (mouse.y - y0) / scale
    }
    // For microphones, branch on bar-mic + expansion state. Thunk gives us access to current
    // state without requiring the MouseHandler to plumb it through every dispatcher signature.
    if (device === 'microphones') {
      dispatch((dispatch2, getState) => {
        const state = getState();
        const area = state.container?.areas?.[state.container?.activeTab];
        const micModel = area?.microphones?.model;
        if (!micModel) return;
        const { micCategories } = constants.equipment;
        const isBar = micCategories.BAR.has(micModel);
        const isCbc = micCategories.CBC.has(micModel);
        if (isBar && !isCbc) {
          const hasExpansion = area.expansionMicType === 'ceiling' || area.expansionMicType === 'table';
          if (!hasExpansion) {
            // first canvas click in add-mode before the user has picked an
            // expansion type. Park the click location so the dialog can place the mic at the
            // exact spot the user clicked, then open the "Add expansion microphones" dialog.
            // (Pre-Flow-B behaviour opened `expansionMicRecommendation` and discarded the
            // click — the user had to click again after the dialog. The new flow honours the
            // original click as the intended placement.)
            dispatch2(ReducersIndex.uiReducers.setPendingExpansionMicLocation(location));
            dispatch2(ReducersIndex.uiReducers.openModal('expansionMicAdd'));
          } else {
            dispatch2(ReducersIndex.container.addExpansionMic({ location }));
          }
          return;
        }
        dispatch2(ReducersIndex.container.addDevice({ device, location }));
      });
      return;
    }

    // Speakers: branch on bar + expansion state. Mirrors the mic-side flow.
    //   - bar + active expansion → add an expansion speaker at the clicked location
    //   - bar + no expansion yet → open the `expansionSpeakerAdd` dialog so the user
    //     can pick ceiling/pendant. The click location is NOT parked (unlike the mic
    //     side's Flow-B); the user clicks again on canvas after the dialog confirms.
    //   - non-bar → normal addDevice path for primary speakers
    if (device === 'speakers') {
      dispatch((dispatch2, getState) => {
        const state = getState();
        const area = state.container?.areas?.[state.container?.activeTab];
        const micModel = area?.microphones?.model;
        if (!micModel) {
          dispatch2(ReducersIndex.container.addDevice({ device, location }));
          return;
        }
        const { micCategories } = constants.equipment;
        const isBar = micCategories.BAR.has(micModel);
        const hasExpansionSpkr = area?.expansionSpeakerType === 'ceiling'
          || area?.expansionSpeakerType === 'pendant';
        if (isBar) {
          if (hasExpansionSpkr) {
            dispatch2(ReducersIndex.container.addExpansionSpeaker({ location }));
          } else {
            dispatch2(ReducersIndex.uiReducers.openModal('expansionSpeakerAdd'));
          }
          return;
        }
        dispatch2(ReducersIndex.container.addDevice({ device, location }));
      });
      return;
    }

    dispatch(ReducersIndex.container.addDevice({ device, location }))
  },

  /**
   * @function remove
   * @description Removes a device from the room based on the clicked element.
   * @param {Event} evt - The click event object
   * @param {Function} dispatch - The Redux dispatch function to trigger actions
   * @param {Object} mouse - The current mouse position (not used in this function but included for consistency with add)
   * @param {Object} drawingBox - Information about the drawing area (not used in this function but included for consistency with add)
   * @param {string} device - The type of device to remove (e.g., 'speaker', 'microphone')
   */
  remove: (evt, dispatch, mouse, drawingBox, device) => {
    const target = evt.target.closest(`[class=${device}]`)?.id;
    if (target) {
      const [, , deviceId] = target.split('-');
      dispatch(ReducersIndex.container.removeDevice({ device, id: deviceId }));
    };
  },

  /**
   * Dispatcher for the delete action.
   * Removes a single expansion microphone OR expansion speaker at the clicked location.
   * Routes based on the SVG class of the clicked render group AND the current device
   * view — the mic view's delete only removes mics, the speaker view's only removes
   * speakers. Without the view gate the closest() walk would happily resolve a speaker
   * click to `removeExpansionSpeaker` even while the user is on the Microphones tab.
   *
   * Both render groups produce element ids of the form `${prefix}-${type}-${index}` (see
   * `layoutArray` in MuiDevices.tsx). The trailing index segment is what the reducer
   * needs to splice the entry from the matching `expansion*.layout`.
   *
   * @param {Event} evt
   * @param {Function} dispatch
   * @param {Object} _mouse - mouse position (unused)
   * @param {Object} _drawingBox - canvas box (unused)
   * @param {string} currentDevice - 'microphones' | 'speakers' | 'cameras' — the active view
   */
  delete: (evt, dispatch, _mouse, _drawingBox, currentDevice) => {
    if (currentDevice === 'microphones') {
      const micTarget = evt.target.closest('[class~=expansionMicrophones]')?.id;
      if (!micTarget) return;
      const segments = micTarget.split('-');
      const index = parseInt(segments[segments.length - 1], 10);
      if (Number.isNaN(index)) return;
      dispatch(ReducersIndex.container.removeExpansionMic({ index }));
      return;
    }

    if (currentDevice === 'speakers') {
      const spkrTarget = evt.target.closest('[class~=expansionSpeakers]')?.id;
      if (!spkrTarget) return;
      const segments = spkrTarget.split('-');
      const index = parseInt(segments[segments.length - 1], 10);
      if (Number.isNaN(index)) return;
      dispatch(ReducersIndex.container.removeExpansionSpeaker({ index }));
    }
  },
};

export default actions;
